import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n';
import { legalPaths, LegalPageType } from '../legal/routes';

interface LegalContentProps {
  type: LegalPageType;
}

const LegalContent: React.FC<LegalContentProps> = ({ type }) => {
  const { t, language } = useLanguage();

  if (type === 'cookies') {
    return (
      <div className="space-y-4">
        <p className="text-gray-700 leading-relaxed whitespace-pre-line">{t.legal.cookies.content}</p>
      </div>
    );
  }

  if (type === 'impressum') {
    const content = t.legal.impressum.content;

    return (
      <div className="space-y-6 text-gray-700">
        <p className="text-sm text-gray-600">{t.legal.impressum.subtitle}</p>

        <div>
          <h2 className="font-bold text-gray-900 text-lg">{content.company}</h2>
          <p className="whitespace-pre-line">{content.address}</p>
          <p>{content.country}</p>
        </div>

        <div>
          <h3 className="font-bold text-gray-900">{content.representedByLabel}</h3>
          <p>{content.representative}</p>
        </div>

        <div>
          <h3 className="font-bold text-gray-900">{content.contactLabel}</h3>
          <p>
            E-Mail:{' '}
            <a href={`mailto:${content.email}`} className="text-primary-600 hover:underline">
              {content.email}
            </a>
          </p>
          <p>Telefon: {content.phone}</p>
        </div>

        <div>
          <h3 className="font-bold text-gray-900">{content.registrationLabel}</h3>
          <p className="whitespace-pre-line">{content.registrationCourt}</p>
          <p>{content.registrationNumber}</p>
        </div>

        <div>
          <h3 className="font-bold text-gray-900">{content.vatLabel}</h3>
          <p>{content.vatNumber}</p>
        </div>

        <div>
          <h3 className="font-bold text-gray-900">{content.responsibleLabel}</h3>
          <p className="text-sm">{content.responsibleDescription}</p>
          <p>{content.responsiblePerson}</p>
        </div>

        <div>
          <h3 className="font-bold text-gray-900">{content.consumerDisputeLabel}</h3>
          <p>{content.consumerDisputeText}</p>
        </div>

        <div>
          <h2 className="font-bold text-gray-900 text-lg">{content.liabilityLabel}</h2>

          <div className="mt-3">
            <h3 className="font-bold text-gray-900">{content.liabilityContentLabel}</h3>
            <p className="text-sm">{content.liabilityContentText}</p>
          </div>

          <div className="mt-3">
            <h3 className="font-bold text-gray-900">{content.liabilityLinksLabel}</h3>
            <p className="text-sm">{content.liabilityLinksText}</p>
          </div>

          <div className="mt-3">
            <h3 className="font-bold text-gray-900">{content.copyrightLabel}</h3>
            <p className="text-sm">{content.copyrightText}</p>
          </div>
        </div>

        <div>
          <h3 className="font-bold text-gray-900">{content.privacyLabel}</h3>
          <p>
            {content.privacyText}{' '}
            <Link to={legalPaths[language].privacy} className="text-primary-600 hover:underline">
              {content.privacyLink}
            </Link>
            .
          </p>
        </div>
      </div>
    );
  }

  const content = t.legal.privacy.content;

  return (
    <div className="space-y-8 text-gray-700">
      <p className="text-sm text-gray-500">{content.lastUpdated}</p>
      <p className="leading-relaxed">{content.intro}</p>
      {content.sections.map((section) => (
        <div key={section.heading}>
          <h2 className="font-bold text-gray-900 mb-2 text-lg">{section.heading}</h2>
          <p className="leading-relaxed whitespace-pre-line">{section.body}</p>
        </div>
      ))}
    </div>
  );
};

export default LegalContent;
